import Konva from "konva";

interface KonvaSnappingParams {
    snapRange?: number;
    guidelineColor?: string;
    guidelineDash?: boolean;
    showGuidelines?: boolean;
    guidelineThickness?: number;
    snapToStageCenter?: boolean;
    snapToStageBorders?: boolean;
    snapToShapes?: boolean;
}

interface Point {
    x: number;
    y: number;
}

interface SnappingPoints {
    vertical: number[];
    horizontal: number[];
}

export const useKonvaSnapping = (params: KonvaSnappingParams) => {
    const defaultParams = {
        snapRange: params.snapRange ?? 3,
        guidelineColor: params.guidelineColor ?? "rgb(0, 161, 255)",
        guidelineDash: params.guidelineDash ?? true,
        showGuidelines: params.showGuidelines ?? true,
        guidelineThickness: params.guidelineThickness ?? 1,
        snapToStageCenter: params.snapToStageCenter ?? true,
        snapToStageBorders: params.snapToStageBorders ?? true,
        snapToShapes: params.snapToShapes ?? true,
    };

    const getSnappingPoints = (
        e: Konva.KonvaEventObject<DragEvent | TouchEvent>,
    ): SnappingPoints => {
        const { snapToStageCenter, snapToStageBorders, snapToShapes } = defaultParams;
        const stage = e.currentTarget.getStage();
        if (!stage) return { vertical: [], horizontal: [] };

        const vertical: number[] = [];
        const horizontal: number[] = [];

        if (snapToStageCenter && stage.attrs.width && stage.attrs.height) {
            vertical.push(stage.attrs.width / 2);
            horizontal.push(stage.attrs.height / 2);
        }
        if (snapToStageBorders && stage.attrs.width && stage.attrs.height) {
            horizontal.push(0, stage.attrs.height);
            vertical.push(0, stage.attrs.width);
        }
        if (snapToShapes) {
            stage.children.forEach((layer: Konva.Layer) => {
                layer.children.forEach((obj: Konva.Node) => {
                    if (obj instanceof Konva.Shape) {
                        const box = obj.getClientRect();

                        if (
                            obj.getType() === "Shape" &&
                            e.target !== obj &&
                            obj.attrs.name !== "guid-line" &&
                            !(obj instanceof Konva.Transformer)
                        ) {
                            vertical.push(box.x, box.x + box.width, box.x + box.width / 2);
                            horizontal.push(box.y, box.y + box.height, box.y + box.height / 2);
                        }
                    }
                });
            });
        }

        return { vertical, horizontal };
    };

    const createLine = (
        layer: Konva.Layer,
        isHorizontal: boolean,
        lineX: number,
        lineY: number,
    ): void => {
        const { guidelineColor, showGuidelines, guidelineThickness, guidelineDash } = defaultParams;
        if (!showGuidelines) return;
        const points = isHorizontal ? [-6000, 0, 6000, 0] : [0, -6000, 0, 6000];
        const line = new Konva.Line({
            points,
            stroke: guidelineColor,
            strokeWidth: guidelineThickness,
            name: "guid-line",
            dash: guidelineDash ? [4, 6] : [0, 0],
        });
        layer.add(line);
        line.absolutePosition({ x: lineX, y: lineY });
    };

    const handleDragging = (e: Konva.KonvaEventObject<DragEvent>): void => {
        const layer = e.target.parent;
        if (!layer || !(layer instanceof Konva.Layer)) return;

        // Clear existing guidelines
        layer.find(".guid-line").forEach((line) => {
            if (line instanceof Konva.Line) {
                line.destroy();
            }
        });

        const { horizontal, vertical } = getSnappingPoints(e);
        let newPos: Point = { x: e.target.absolutePosition().x, y: e.target.absolutePosition().y };
        const guideLinesX: number[] = [];
        const guideLinesY: number[] = [];
        const { snapRange } = defaultParams;

        // Snap vertically
        vertical.forEach((breakPoint: number) => {
            if (Math.abs(e.target.getClientRect().x - breakPoint) <= snapRange) {
                newPos.x = breakPoint + e.target.absolutePosition().x - e.target.getClientRect().x;
                guideLinesX.push(breakPoint);
            }
            if (
                Math.abs(
                    e.target.getClientRect().x - breakPoint + e.target.getClientRect().width / 2,
                ) <= snapRange
            ) {
                newPos.x =
                    breakPoint +
                    e.target.absolutePosition().x -
                    e.target.getClientRect().x -
                    e.target.getClientRect().width / 2;
                guideLinesX.push(breakPoint);
            }
            if (
                Math.abs(
                    e.target.getClientRect().x - breakPoint + e.target.getClientRect().width,
                ) <= snapRange
            ) {
                newPos.x =
                    breakPoint +
                    e.target.absolutePosition().x -
                    e.target.getClientRect().x -
                    e.target.getClientRect().width;
                guideLinesX.push(breakPoint);
            }
        });
        e.target.absolutePosition(newPos);
        guideLinesX.forEach((line: number) => {
            if (
                Math.round(e.target.getClientRect().x - line) === 0 ||
                Math.round(
                    e.target.getClientRect().x - line + e.target.getClientRect().width / 2,
                ) === 0 ||
                Math.round(e.target.getClientRect().x - line + e.target.getClientRect().width) === 0
            ) {
                createLine(layer, false, line, 0);
            }
        });

        // Snap horizontally
        horizontal.forEach((breakPoint: number) => {
            if (Math.abs(e.target.getClientRect().y - breakPoint) <= snapRange) {
                newPos.y = breakPoint + e.target.absolutePosition().y - e.target.getClientRect().y;
                guideLinesY.push(breakPoint);
            }
            if (
                Math.abs(
                    e.target.getClientRect().y - breakPoint + e.target.getClientRect().height,
                ) <= snapRange
            ) {
                newPos.y =
                    breakPoint +
                    e.target.absolutePosition().y -
                    e.target.getClientRect().y -
                    e.target.getClientRect().height;
                guideLinesY.push(breakPoint);
            }
            if (
                Math.abs(
                    e.target.getClientRect().y - breakPoint + e.target.getClientRect().height / 2,
                ) <= snapRange
            ) {
                newPos.y =
                    breakPoint +
                    e.target.absolutePosition().y -
                    e.target.getClientRect().y -
                    e.target.getClientRect().height / 2;
                guideLinesY.push(breakPoint);
            }
        });
        e.target.absolutePosition(newPos);
        guideLinesY.forEach((line: number) => {
            if (
                Math.round(e.target.getClientRect().y - line) === 0 ||
                Math.round(e.target.getClientRect().y - line + e.target.getClientRect().height) ===
                    0 ||
                Math.round(
                    e.target.getClientRect().y - line + e.target.getClientRect().height / 2,
                ) === 0
            ) {
                createLine(layer, true, 0, line);
            }
        });
    };

    const handleResizing = (e: Konva.KonvaEventObject<Event>): void => {
        const layer = e.target.parent;
        if (!layer || !(layer instanceof Konva.Layer)) return;

        const { snapRange } = defaultParams;
        const { horizontal, vertical } = getSnappingPoints(e as Konva.KonvaEventObject<DragEvent>);

        // Clear existing guidelines
        layer.find(".guid-line").forEach((line) => {
            if (line instanceof Konva.Line) {
                line.destroy();
            }
        });

        // Get the shape being transformed
        const shape = e.target;
        const box = shape.getClientRect();

        // Check snapping points
        vertical.forEach((breakPoint: number) => {
            // Snap left edge
            if (Math.abs(box.x - breakPoint) <= snapRange) {
                createLine(layer, false, breakPoint, 0);
            }
            // Snap center
            if (Math.abs(box.x + box.width / 2 - breakPoint) <= snapRange) {
                createLine(layer, false, breakPoint, 0);
            }
            // Snap right edge
            if (Math.abs(box.x + box.width - breakPoint) <= snapRange) {
                createLine(layer, false, breakPoint, 0);
            }
        });

        horizontal.forEach((breakPoint: number) => {
            // Snap top edge
            if (Math.abs(box.y - breakPoint) <= snapRange) {
                createLine(layer, true, 0, breakPoint);
            }
            // Snap middle
            if (Math.abs(box.y + box.height / 2 - breakPoint) <= snapRange) {
                createLine(layer, true, 0, breakPoint);
            }
            // Snap bottom edge
            if (Math.abs(box.y + box.height - breakPoint) <= snapRange) {
                createLine(layer, true, 0, breakPoint);
            }
        });
    };

    const handleResizeEnd = (e: Konva.KonvaEventObject<Event>): void => {
        const layer = e.target.parent;
        if (!layer || !(layer instanceof Konva.Layer)) return;

        // Clear guidelines
        layer.find(".guid-line").forEach((line) => {
            if (line instanceof Konva.Line) {
                line.destroy();
            }
        });
    };

    const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>): void => {
        const layer = e.target.parent;
        if (!layer || !(layer instanceof Konva.Layer)) return;

        layer.find(".guid-line").forEach((line) => {
            if (line instanceof Konva.Line) {
                line.destroy();
            }
        });
    };

    return { handleDragging, handleResizing, handleResizeEnd, handleDragEnd };
};
